import {
  type DefaultError, type MutationFunction,
  type MutationOptions, type QueryClient, type QueryKey,
  useMutation,
  useQueryClient
} from '@tanstack/vue-query'

type Options<TData, TVariables> = MutationOptions<TData, DefaultError, TVariables> & { invalidateOn?: 'success'|'error'|'all' }
type Key<TVariables> = (QueryKey|((input: TVariables) => QueryKey|undefined))

const invalidateKeys = <TVariables>(client: QueryClient, input: TVariables, keys: Key<TVariables>[]) => Promise.all(
  keys.map(
    queryKey => {
      if (Array.isArray(queryKey)) {
        return client.invalidateQueries({ queryKey })
      }

      if (typeof queryKey === 'function') {
        const resolvedKey = queryKey(input)

        if (resolvedKey !== undefined) {
          return client.invalidateQueries({ queryKey: resolvedKey })
        }
      }

      return Promise.resolve()
    }
  )
)

export function useMutationWithInvalidation<TData, TVariables>(options: MutationFunction<TData, TVariables>|Options<TData, TVariables>, keys: Key<TVariables>[]) {
  const client = useQueryClient()
  const optionsObject: Options<TData, TVariables> = typeof options === 'function'
    ? { mutationFn: options, invalidateOn: 'success' }
    : { ...options }

  const { invalidateOn = 'success' } = optionsObject

  return useMutation({
    ...optionsObject,
    async onSuccess(output, input, context) {
      if (invalidateOn === 'success' || invalidateOn === 'all') {
        await invalidateKeys(client, input, keys)
      }

      return optionsObject.onSuccess?.(output, input, context)
    },
    async onError(error, variables, context) {
      if (invalidateOn === 'error' || invalidateOn === 'all') {
        await invalidateKeys(client, variables, keys)
      }

      return optionsObject.onError?.(error, variables, context)
    }
  })
}
